import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, queryClient, tokenStore, unwrap } from './api';

export interface Member {
  id: string;
  displayName: string;
  initials: string;
  avatarColour: string;
  email: string;
  firstName: string;
  bio: string | null;
  ratingAverage: number | null;
  reviewCount: number;
  responseRatePct: number;
  memberSince: string;
  verifiedBadges: string[];
}

interface Session {
  member: Member | null;
  /** True until the stored refresh token has been tried on first load. */
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  register: (details: Registration) => Promise<void>;
  signOut: () => Promise<void>;
}

/**
 * What sign-up asks for.
 *
 * `gender` is deliberately absent: the server defaults it to `UNDISCLOSED` and
 * every other preference belongs on the profile screen, where it can be changed
 * later. A sign-up form that asks for everything is a sign-up form people
 * abandon.
 */
export interface Registration {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

export const SessionContext = createContext<Session | null>(null);

/**
 * Did this request fail because the session is gone?
 *
 * The client already tries one refresh on a 401 and clears the tokens when that
 * fails, so a 401 reaching a screen means there is no session left — not that
 * the access token merely expired.
 */
export function isSessionExpired(error: unknown): boolean {
  return (error as { status?: number } | null | undefined)?.status === 401;
}

/**
 * Ends the React session when a screen's own requests say it is already over.
 *
 * Without this the token store is empty while `member` still holds whoever
 * signed in, so the header keeps showing their avatar and a protected screen
 * sits on an error it cannot recover from. Clearing the member lets
 * `RequireMember` do what it exists for and send them to sign in.
 *
 * Returns whether the session had expired, so the caller can render something
 * other than a permanent spinner in the frame before the redirect.
 */
export function useEndSessionOnAuthFailure(...errors: unknown[]): boolean {
  const { signOut } = useSession();
  const expired = errors.some(isSessionExpired);

  useEffect(() => {
    if (expired) void signOut();
  }, [expired, signOut]);

  return expired;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [member, setMember] = useState<Member | null>(null);
  const [loading, setLoading] = useState(true);

  // On first load there is no access token, only a possible refresh token in
  // storage. Calling /auth/me lets the client's 401 interceptor exchange it —
  // so a returning member is signed in without a round-trip of their own.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!(await tokenStore.getRefreshToken())) {
        if (!cancelled) setLoading(false);
        return;
      }

      try {
        const me = await unwrap(api.GET('/auth/me'));
        if (!cancelled) setMember(me as Member);
      } catch {
        if (!cancelled) setMember(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Everything cached belonged to whoever was signed in.
   *
   * `me`, `bookings/mine` and `trips/mine` are keyed by what they are, not by
   * whose they are — there is only ever one member in a tab. That holds right
   * up to the moment it does not: sign out, sign in as somebody else, and the
   * thirty-second stale time hands the new member the old member's profile and
   * trips while the refetch is still in flight.
   *
   * Clearing on both ends of the transition is the whole fix. It also drops
   * `places` and `popular-routes`, which belong to nobody — a wasted refetch is
   * a better trade than a key-by-key list somebody has to remember to extend.
   */
  const forgetEverything = useCallback(() => {
    queryClient.clear();
  }, []);

  /**
   * The tail both ways in share: clear whatever the last member left, store the
   * tokens, and adopt the new member. Sign-up differs from sign-in only in
   * which endpoint issued the session.
   */
  const adopt = useCallback(
    async (session: { accessToken: string; refreshToken: string; user: unknown }) => {
      forgetEverything();
      await tokenStore.setTokens({
        accessToken: session.accessToken,
        refreshToken: session.refreshToken,
      });
      setMember(session.user as Member);
    },
    [forgetEverything],
  );

  const signIn = useCallback(
    async (email: string, password: string) => {
      await adopt(await unwrap(api.POST('/auth/login', { body: { email, password } })));
    },
    [adopt],
  );

  const register = useCallback(
    async (details: Registration) => {
      await adopt(await unwrap(api.POST('/auth/register', { body: details })));
    },
    [adopt],
  );

  const signOut = useCallback(async () => {
    const refreshToken = await tokenStore.getRefreshToken();
    if (refreshToken) {
      // Best effort: the local session ends either way.
      await api.POST('/auth/logout', { body: { refreshToken } }).catch(() => undefined);
    }
    await tokenStore.setTokens(null);
    setMember(null);
    forgetEverything();
  }, [forgetEverything]);

  const value = useMemo(
    () => ({ member, loading, signIn, register, signOut }),
    [member, loading, signIn, register, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (!session) throw new Error('useSession must be used inside a SessionProvider');
  return session;
}
